import { Box, Typography, Button, FormControl } from "@mui/material";
import { InputBase } from "components/common/Input";
import { useFormik } from "formik";
import useSaml, { api } from "hooks/saml/useSaml";
import theme from "lib/theme";
import { useNavigate, useParams } from "react-router-dom";
import { SamlSetting } from "types/samlSetting";
import { useState } from "react";
import { AttachFile } from "@mui/icons-material";
import useDirectUpload from "hooks/useDirectUpload";

const samlSetting: SamlSetting = {
  idpSsoServiceUrl: "",
};

export function ConfigureProvider() {
  const navigate = useNavigate();
  const params = useParams();
  const { createSamlSettings } = useSaml();
  const [certName, setCertName] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);
  const { uploadAsync, loading } = useDirectUpload({ keyPrefix: "saml_settings" });

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;

    const file = event.target.files[0];
    setCertName(file.name);

    try {
      const blob = await uploadAsync(file);
      if (blob) {
        setFileUrl(blob.signed_id);
      }
    } catch (error) {
      console.error("Upload error:", error);
    }
  };

  const triggerFileInput = () => {
    if (inputRef) {
      inputRef.click();
    }
  };

  const formik = useFormik({
    initialValues: samlSetting,
    onSubmit: (values: any) => {
      if (fileUrl) {
        values["samlCertificate"] = fileUrl;
      }
      values["provider"] = params.provider;

      createSamlSettings(values);
    },
  });

  const showSave = () => {
    if (formik.values.idpSsoServiceUrl && fileUrl) {
      return false;
    } else {
      return true;
    }
  };

  return (
    <div>
      <Box
        mt={4}
        sx={{
          px: 5,
          py: 3,
          background: "white",
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6" fontWeight="600">
          {`Configure SAML authentication for ${params.provider}`}
        </Typography>
        <Typography>
          Add the information below to enable single sign on for the Revivn platform with your provider.
        </Typography>

        <form onSubmit={formik.handleSubmit}>
          <Box mt={4}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <FormControl
                variant="standard"
                sx={{ display: "grid", gridTemplateColumns: "290px 1fr", gap: 4, alignItems: "end" }}
              >
                <label htmlFor="sso-url">
                  <Typography fontWeight="600">SAML SSO URL</Typography>
                  <Typography>Enter your SAML 2.0 Endpoint Login URL</Typography>
                </label>
                <InputBase
                  name="idpSsoServiceUrl"
                  value={formik.values.idpSsoServiceUrl}
                  onChange={formik.handleChange}
                />
              </FormControl>

              <FormControl
                variant="standard"
                sx={{ display: "grid", gridTemplateColumns: "290px 1fr", gap: 4, alignItems: "end" }}
              >
                <label htmlFor="public-certificate">
                  <Typography fontWeight="600">Public Certificate</Typography>
                  <Typography>Attach your Public Certificate here (.pem or .cert files accepted)</Typography>
                </label>
                <Box mt={3}>
                  <input
                    type="file"
                    accept=".pem,.cert,.csv"
                    style={{ display: "none" }}
                    onChange={handleFileUpload}
                    ref={(ref) => setInputRef(ref)}
                  />
                  <Button variant="outlined" color="primary" size="large" onClick={triggerFileInput} disabled={loading}>
                    <AttachFile color="primary" />
                    {loading ? "Uploading..." : certName ? "Edit" : "Attach Your Certificate Here"}
                  </Button>
                  {certName && <Box>{`${certName} is attached`}</Box>}
                </Box>
              </FormControl>
            </Box>
            <Box sx={{ mt: 5, display: "flex", gap: 2 }}>
              <Button size="large" variant="outlined" onClick={() => navigate(-1)}>
                Back
              </Button>
              <Button size="large" variant="contained" type="submit" disabled={showSave()}>
                Save
              </Button>
            </Box>
          </Box>
        </form>
      </Box>
    </div>
  );
}
